// src/components/SectionManager.js
import React, { useState, useCallback, useEffect, useContext } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from './ui/card';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import { Loader2, Plus, Edit, Trash2, Save, XCircle, CheckCircle, UserPlus, Users } from 'lucide-react'; // Added UserPlus, Users icons
import { cn } from '../lib/utils';

// Import Firestore functions and AuthContext
import { db, collection, addDoc, doc, setDoc, deleteDoc, getDocs, Timestamp, query, where } from '../firebase/firebase';
import { AuthContext } from '../App'; // Assuming AuthContext is exported from App.js

const SectionManager = ({ onSectionAddedOrUpdated }) => {
    const currentUser = useContext(AuthContext);
    const teacherId = currentUser ? currentUser.uid : null;

    const [sectionForm, setSectionForm] = useState({
        id: '', // For editing or auto-generating
        name: '',
        description: '',
        studentUids: [], // Array to store UIDs of enrolled students
    });
    const [sectionsList, setSectionsList] = useState([]);
    const [allUsers, setAllUsers] = useState([]); // State to hold all registered users (potential students)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [editMode, setEditMode] = useState(false); // True if editing an existing section

    // --- Helper function for custom confirmation dialog ---
    const showConfirmDialog = (message) => {
        return new Promise((resolve) => {
            const dialog = document.createElement('div');
            dialog.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
            dialog.innerHTML = `
                <div class="bg-mad-dark-green p-6 rounded-lg shadow-xl text-white max-w-sm w-full space-y-4"> {/* Theme color */}
                    <h3 class="text-xl font-semibold text-mad-yellow-accent">Confirm Action</h3> {/* Theme color */}
                    <p>${message}</p>
                    <div class="flex justify-end gap-3">
                        <button id="cancel-btn" class="px-4 py-2 bg-gray-600 rounded-full hover:bg-gray-700 transition-colors">Cancel</button> {/* Rounded button */}
                        <button id="confirm-btn" class="px-4 py-2 bg-mad-orange rounded-full hover:bg-mad-orange/80 transition-colors">Confirm</button> {/* Theme color */}
                    </div>
                </div>
            `;
            document.body.appendChild(dialog);

            document.getElementById('confirm-btn').onclick = () => {
                document.body.removeChild(dialog);
                resolve(true);
            };
            document.getElementById('cancel-btn').onclick = () => {
                document.body.removeChild(dialog);
                resolve(false);
            };
        });
    };

    // Helper to get user display name from UID - MOVED UP
    const getUserDisplayName = useCallback((uid) => {
        const user = allUsers.find(u => u.uid === uid);
        return user ? (user.displayName || user.email) : uid; // Fallback to UID if not found
    }, [allUsers]);

    // Fetch sections managed by the current teacher
    const fetchSections = useCallback(async () => {
        if (!teacherId) return; // Don't fetch if no teacher is logged in

        setLoading(true);
        setError(null);
        try {
            const sectionsCollectionRef = collection(db, 'sections');
            // Query only sections created by the current teacher
            const q = query(sectionsCollectionRef, where('teacherId', '==', teacherId));
            const querySnapshot = await getDocs(q);
            const loadedSections = [];
            querySnapshot.forEach(doc => {
                loadedSections.push({ id: doc.id, ...doc.data() });
            });
            // Sort by creation date
            loadedSections.sort((a, b) => (a.createdAt?.toDate() || 0) - (b.createdAt?.toDate() || 0));
            setSectionsList(loadedSections);
        } catch (fetchError) {
            console.error("Error fetching sections:", fetchError);
            setError(`Failed to load sections: ${fetchError.message}`);
        } finally {
            setLoading(false);
        }
    }, [teacherId]);

    // NEW: Function to fetch all registered users (for enrollment)
    const fetchAllUsers = useCallback(async () => {
        setLoading(true); // Can use a separate loading state for users if desired
        setError(null);
        try {
            // Assuming a 'users' collection exists and stores basic user profiles (e.g., created on first login)
            const usersCollectionRef = collection(db, 'users');
            const q = query(usersCollectionRef); // Fetch all users
            const querySnapshot = await getDocs(q);
            const loadedUsers = [];
            querySnapshot.forEach(doc => {
                const userData = doc.data();
                // Filter out the teacher themselves from the student list
                if (doc.id !== teacherId) {
                    loadedUsers.push({ uid: doc.id, email: userData.email, displayName: userData.displayName });
                }
            });
            setAllUsers(loadedUsers);
        } catch (fetchError) {
            console.error("Error fetching all users:", fetchError);
            setError(`Failed to load user list: ${fetchError.message}`);
        } finally {
            setLoading(false);
        }
    }, [teacherId]); // Depend on teacherId to exclude self

    useEffect(() => {
        fetchSections();
        // Only fetch all users if the current user is a teacher
        if (teacherId) {
            fetchAllUsers();
        }
    }, [fetchSections, fetchAllUsers, teacherId]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setSectionForm(prev => ({ ...prev, [name]: value }));
    }, []);

    // NEW: Handle changes to student selection
    const handleStudentSelectionChange = useCallback((e) => {
        const selectedOptions = Array.from(e.target.selectedOptions).map(option => option.value);
        setSectionForm(prev => ({ ...prev, studentUids: selectedOptions }));
    }, []);

    const resetForm = useCallback(() => {
        setSectionForm({
            id: '',
            name: '',
            description: '',
            studentUids: [],
        });
        setEditMode(false);
        setError(null);
        setSuccessMessage('');
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccessMessage('');

        if (!teacherId) {
            setError("No teacher ID found. Please log in as a teacher.");
            setLoading(false);
            return;
        }

        try {
            const sectionData = {
                name: sectionForm.name.trim(),
                description: sectionForm.description.trim(),
                teacherId: teacherId, // Assign current teacher as manager
                studentUids: sectionForm.studentUids, // Save the selected student UIDs
            };

            if (!sectionData.name) {
                throw new Error("Section name cannot be empty.");
            }

            // --- Enforce "student in only 1 section" rule ---
            for (const studentUid of sectionData.studentUids) {
                // Check if this student is already in any other section (not the one being edited)
                const existingSectionForStudent = sectionsList.find(
                    (section) =>
                        section.id !== sectionForm.id && // Not the current section being edited
                        section.studentUids &&
                        section.studentUids.includes(studentUid)
                );

                if (existingSectionForStudent) {
                    const studentDisplayName = getUserDisplayName(studentUid);
                    throw new Error(
                        `Student '${studentDisplayName}' is already enrolled in section '${existingSectionForStudent.name}'. ` +
                        `A student can only be in one section. Please remove them from the existing section first.`
                    );
                }
            }
            // --- End enforcement ---


            if (editMode && sectionForm.id) {
                const sectionRef = doc(db, 'sections', sectionForm.id);
                await setDoc(sectionRef, { ...sectionData, updatedAt: Timestamp.now() }, { merge: true });
                setSuccessMessage(`Section "${sectionData.name}" updated successfully!`);
            } else {
                // For new sections, add to collection and let Firestore generate ID
                const docRef = await addDoc(collection(db, 'sections'), { ...sectionData, createdAt: Timestamp.now(), updatedAt: Timestamp.now() });
                setSuccessMessage(`Section "${sectionData.name}" added successfully with ID: ${docRef.id}`);
            }

            resetForm();
            fetchSections(); // Refresh the list of sections
            if (onSectionAddedOrUpdated) {
                onSectionAddedOrUpdated(); // Notify parent component (CodeExecutorApp) if needed
            }

        } catch (submitError) {
            console.error("Error saving section:", submitError);
            setError(`Failed to save section: ${submitError.message}`);
        } finally {
            setLoading(false);
        }
    }, [editMode, sectionForm, teacherId, fetchSections, resetForm, onSectionAddedOrUpdated, sectionsList, getUserDisplayName]); // Added sectionsList and getUserDisplayName to dependencies

    const handleEditSectionClick = useCallback((sectionToEdit) => {
        setSectionForm({
            id: sectionToEdit.id,
            name: sectionToEdit.name,
            description: sectionToEdit.description,
            studentUids: sectionToEdit.studentUids || [], // Ensure it's an array for consistency
        });
        setEditMode(true);
        setError(null);
        setSuccessMessage('');
    }, []);

    const handleDeleteSection = useCallback(async (sectionIdToDelete) => {
        const isConfirmed = await showConfirmDialog(`Are you sure you want to delete this section? This action cannot be undone.`);
        if (!isConfirmed) {
            return;
        }

        setLoading(true);
        setError(null);
        setSuccessMessage('');

        try {
            await deleteDoc(doc(db, 'sections', sectionIdToDelete));
            setSuccessMessage(`Section deleted successfully!`);
            fetchSections(); // Refresh the list of sections
            if (onSectionAddedOrUpdated) {
                onSectionAddedOrUpdated();
            }
            if (sectionForm.id === sectionIdToDelete) { // If deleting the section currently being edited
                resetForm();
            }
        } catch (deleteError) {
            console.error("Error deleting section:", deleteError);
            setError(`Failed to delete section: ${deleteError.message}`);
        } finally {
            setLoading(false);
        }
    }, [fetchSections, onSectionAddedOrUpdated, sectionForm.id, resetForm, showConfirmDialog]);

    return (
        <Card className="bg-mad-card-bg backdrop-blur-md border border-mad-border-color shadow-lg rounded-xl"> {/* Theme color */}
            <CardHeader>
                <CardTitle className="text-lg sm:text-xl text-mad-yellow-accent flex items-center gap-2"> {/* Theme color */}
                    <Edit className="w-5 h-5 text-mad-orange" /> {/* Theme color */}
                    {editMode ? 'Edit Section' : 'Create New Section'}
                </CardTitle>
                <CardDescription className="text-gray-400">
                    {editMode ? 'Modify details and enrolled students for an existing section.' : 'Define a new course section and enroll students.'}
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {error && (
                    <Alert variant="destructive" className="bg-red-500/10 text-red-400 border-red-500/50 rounded-lg">
                        <XCircle className="h-4 w-4" />
                        <AlertTitle>Error</AlertTitle>
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}
                {successMessage && (
                    <Alert variant="success" className="bg-green-500/10 text-green-400 border-green-500/50 rounded-lg">
                        <CheckCircle className="h-4 w-4" />
                        <AlertTitle>Success</AlertTitle>
                        <AlertDescription>{successMessage}</AlertDescription>
                    </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">Section Name:</label>
                        <Input
                            id="name"
                            type="text"
                            name="name"
                            value={sectionForm.name}
                            onChange={handleChange}
                            placeholder="e.g., CS101 - Fall 2024"
                            className="bg-black/80 text-white border-mad-border-color rounded-md" /* Theme color */
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-1">Description (Optional):</label>
                        <Textarea
                            id="description"
                            name="description"
                            value={sectionForm.description}
                            onChange={handleChange}
                            placeholder="A brief description of this section."
                            className="min-h-[80px] bg-black/80 text-white border-mad-border-color rounded-md" /* Theme color */
                        />
                    </div>

                    {/* NEW: Student Enrollment Section */}
                    <div>
                        <label htmlFor="studentUids" className="block text-sm font-medium text-gray-300 mb-1 flex items-center gap-1">
                            <Users className="w-4 h-4 text-mad-yellow-accent" /> Select Students: {/* Theme color */}
                        </label>
                        <select
                            id="studentUids"
                            name="studentUids"
                            multiple
                            value={sectionForm.studentUids}
                            onChange={handleStudentSelectionChange}
                            className="w-full min-h-[150px] bg-black/80 text-white border-mad-border-color rounded-md
                                       focus:outline-none focus:ring-2 focus:ring-mad-orange focus:border-transparent
                                       p-2" /* Theme color */
                            size="8" // Display multiple options without scrollbar initially
                        >
                            {allUsers.length === 0 ? (
                                <option disabled>No other registered users found.</option>
                            ) : (
                                allUsers.map(user => (
                                    <option key={user.uid} value={user.uid}>
                                        {user.displayName || user.email} (UID: {user.uid})
                                    </option>
                                ))
                            )}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple students.</p>
                        
                        {/* Display selected students' names/emails */}
                        {sectionForm.studentUids.length > 0 && (
                            <div className="mt-2 p-2 bg-mad-dark-green/50 rounded-md border border-mad-border-color">
                                <p className="text-sm font-medium text-mad-yellow-accent mb-1">Currently Selected:</p>
                                <ul className="list-disc list-inside text-gray-300 text-sm">
                                    {sectionForm.studentUids.map(uid => (
                                        <li key={uid}>{getUserDisplayName(uid)}</li>
                                    ))}
                                
                                </ul>
                            </div>
                        )}
                    </div>


                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            onClick={resetForm}
                            variant="outline"
                            className="text-gray-300 border-gray-300 hover:bg-gray-300/20 hover:text-gray-100 rounded-full" /* Rounded button */
                            disabled={loading}
                        >
                            Cancel / New Section
                        </Button>
                        <Button
                            type="submit"
                            className={cn(
                                "bg-mad-orange text-white px-6 py-3 rounded-full shadow-lg", /* Theme color */
                                "hover:bg-mad-orange/80 transition-colors duration-200",
                                "disabled:opacity-50 disabled:cursor-not-allowed",
                                "flex items-center gap-1"
                            )}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="animate-spin w-4 h-4 mr-2" /> Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4 mr-2" /> {editMode ? 'Update Section' : 'Add Section'}
                                </>
                            )}
                        </Button>
                    </div>
                </form>

                <div className="mt-8 pt-4 border-t border-mad-border-color"> {/* Theme color */}
                    <h3 className="text-md font-semibold text-gray-200 mb-4">Your Sections</h3>
                    <ul className="space-y-2 overflow-y-auto max-h-[300px] pr-2">
                        {sectionsList.length === 0 ? (
                            <p className="text-gray-500 italic">No sections created yet.</p>
                        ) : (
                            sectionsList.map(section => (
                                <li key={section.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white/5 p-3 rounded-lg border border-mad-border-color"> {/* Theme color */}
                                    <div className="flex-1">
                                        <span className="text-gray-200 font-medium">{section.name} (ID: {section.id})</span>
                                        {/* Display enrolled student names/emails */}
                                        {section.studentUids && section.studentUids.length > 0 ? (
                                            <p className="text-gray-400 text-sm mt-1">
                                                Enrolled: {section.studentUids.map(uid => getUserDisplayName(uid)).join(', ')}
                                            </p>
                                        ) : (
                                            <p className="text-gray-400 text-sm mt-1">No students enrolled.</p>
                                        )}
                                    </div>
                                    <div className="flex gap-2 mt-2 sm:mt-0">
                                        <Button
                                            onClick={() => handleEditSectionClick(section)}
                                            variant="outline"
                                            size="sm"
                                            className="text-mad-orange border-mad-orange hover:bg-mad-orange/20 hover:text-white rounded-full" /* Theme color */
                                        >
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            onClick={() => handleDeleteSection(section.id)}
                                            variant="destructive"
                                            size="sm"
                                            className="text-red-300 border-red-300 hover:bg-red-300/20 hover:text-red-100 rounded-full" /* Rounded button */
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            </CardContent>
        </Card>
    );
};

export default SectionManager;
